import dayjs, { type Dayjs } from 'dayjs';
import {
  useRef,
  type ReactElement,
  useEffect,
  useState,
  Fragment,
} from 'react';
import { Event } from '../Events/Event';
import { type CalendarEvent, type CalendarDay } from '../Calendar';

const toDayjs = (s: string, e: string, d: string) => {
  const start = dayjs(`${d}T${s}`);
  const end = dayjs(`${d}T${e}`);
  const date = dayjs(d);
  return { start, end, date };
};

interface Props {
  rowHeight: number;
  days: CalendarDay[];
  startDate: Dayjs;
}

export const EventLayer = (props: Props): ReactElement => {
  const ref = useRef<HTMLDivElement>(null);
  const { rowHeight, days, startDate } = props;
  const [rowWidth, setRowWidth] = useState<number>(0);

  useEffect(() => {
    const updateWidth = () => {
      if (ref.current) {
        setRowWidth(ref.current.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const getPosition = (event: CalendarEvent, dateString: string) => {
    const { start, date } = toDayjs(event.start, event.end, dateString);
    const diffInDays = date.diff(startDate, 'day');
    const y = diffInDays * rowHeight;

    const minutes = start.minute();
    const hours = start.hour() + minutes / 60;

    const x = Math.floor(hours * (rowWidth / 24));
    return { x, y };
  };

  const getSize = (event: CalendarEvent, dateString: string) => {
    const { start, end } = toDayjs(event.start, event.end, dateString);
    const diffInMinutes = end.diff(start, 'minute');

    const width = (diffInMinutes / 60) * (rowWidth / 24);
    const height = rowHeight;

    return { width, height };
  };

  return (
    <div ref={ref} className="absolute top-0 left-0 right-0 bottom-0 z-10">
      {days.map(day => (
        <Fragment key={`day-row-${day.date}`}>
          {day.events?.map(event => (
            <Fragment key={`${event.start}-events`}>
              <Event
                x={getPosition(event, day.date).x}
                y={getPosition(event, day.date).y}
                height={getSize(event, day.date).height}
                width={getSize(event, day.date).width}
                color={event.color}
                label={event.label}
                tooltip={event.tooltip}
                onClick={event.onClick}
              />
            </Fragment>
          ))}
        </Fragment>
      ))}
    </div>
  );
};
